import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentHandler } from '../components/submitHandlers';
import CheckoutFormPages from "./CheckoutForm";
import useNavigationWarning from '../hooks/useNavigationWarning';
import csrfService from '../services/csrfService';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
  : null;

const isDevelopment = process.env.REACT_APP_ENV === 'development';
const STRIPE_SERVER_URL = isDevelopment
  ? 'http://localhost:4000/node'
  : process.env.REACT_APP_STRIPE_SERVER_URL;

const BETA_TESTING = (process.env.REACT_APP_BETA_TESTING || 'false').toLowerCase() === 'true';

const amountLookup = {
  'streamline': 999,
  'tailor': 199
};

// Plausible product ID mapping
const productIdMap = {
  'streamline': 1,
  'tailor': 2
};

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState(null);
  const [intentID, setIntentID] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [preparedFormData, setPreparedFormData] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [smallScreenNoticeDismissed, setSmallScreenNoticeDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('paymentSmallScreenNoticeDismissed') === 'true';
    } catch (e) {
      return false;
    }
  });

  // Use a ref to track if we've already created a payment intent
  const paymentIntentCreated = useRef(false);

  // Use a ref to track if we've already fired the checkout started event
  const checkoutStartedTracked = useRef(false);

  // Warn users before leaving the payment page (allow forward to processing)
  useNavigationWarning(true, 'Your payment is in progress. Are you sure you want to leave?', {
    allowPaths: ['/processing'],
  });

  // Detect small screens so we can suggest using a larger device for checkout.
  useEffect(() => {
    let resizeTimer;

    const checkSmallScreen = () => {
      setIsSmallScreen(window.innerWidth <= 1024);
    };

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkSmallScreen, 100);
    };

    checkSmallScreen();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const dismissSmallScreenNotice = () => {
    setSmallScreenNoticeDismissed(true);
    try {
      sessionStorage.setItem('paymentSmallScreenNoticeDismissed', 'true');
    } catch (e) {
      // noop: sessionStorage unavailable (private mode etc.)
    }
  };

  const { formDataEntries, activeModal, betaInfo } = location.state || {};

  // Track "Checkout Started" event on component mount
  useEffect(() => {
    if (!activeModal || checkoutStartedTracked.current) return;

    checkoutStartedTracked.current = true;

    if (typeof window.plausible === 'function') {
      window.plausible('Checkout Started', {
        props: {
          product: productIdMap[activeModal],
          product_name: activeModal
        }
      });
    }
  }, [activeModal]); // Fire when activeModal is available
  // Replace your current amountLookup with this function
  const getAmount = (modalType, promoInfo) => {
    const baseAmount = amountLookup[modalType] || 999;
    // If it's a free promo
    if (promoInfo && promoInfo.type === 'free') {
      return 0;
    }

    if (promoInfo && promoInfo.type === 'multiply') {
      const discountAmount = Math.round(baseAmount * promoInfo.value);
      return discountAmount;
    }

    if (promoInfo && promoInfo.type === 'reduce') {
      //math.max(0, baseAmount - promoInfo.value)
      const discountAmount = Math.max(0, Math.round(baseAmount - promoInfo.value));
      return discountAmount;
    }



    // Default to regular price
    return baseAmount;
  };

  useEffect(() => {
    if (!formDataEntries || !activeModal) {
      navigate('/');
      return;
    }

    // If we've already created a payment intent, don't create another one
    if (promoInfo && promoInfo.current) {
      return;
    }

    const initializePayment = async () => {
      try {
        // Mark that we're creating a payment intent
        setPromoInfo(prev => ({
          ...prev,
          current: true,
        }));
        setIsLoading(true);

        // Reconstruct FormData from the entries array
        const formData = new FormData();
        for (const [key, value] of formDataEntries) {
          formData.append(key, value);
        }

        if (!BETA_TESTING) {
          const result = await PaymentHandler.handleStripePayment(
            activeModal,
            promoInfo,
            STRIPE_SERVER_URL
          );

          if (result.error) {
            // Reset the flag on error so user can retry
            setPromoInfo(prev => ({
              ...prev,
              current: false,
            }));

            // Check if it's a session expiration error
            if (result.error === 'CSRF token validation failed' ||
              result.error === 'Invalid or expired CSRF token') {
              setError('Your session has expired. Please refresh the page to continue.');
            } else {
              // Handle other errors
              throw new Error(result.error);
            }
          }


          // Append intentID to formData ONCE
          formData.append("intentID", result.intent_id);

          setIntentID(result.intent_id);
          setClientSecret(result.client_secret);
          setPreparedFormData(formData);
        } else {
          // For beta testing, skip to processing
          formData.append("intentID", "promo");
          setIntentID("promo");
          setPreparedFormData(formData);
        }
      } catch (error) {
        // Reset the flag on error so user can retry
        paymentIntentCreated.current = false;
        setError(`Failed to initialize payment: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [formDataEntries, activeModal, navigate, promoInfo]); // Remove the other dependencies

  useEffect(() => {
    // Add a style tag directly to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .app-container {
        background: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-image: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-attachment: fixed !important;
      }
      
      .app-container::before,
      .app-container::after {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Updated to accept the actual payment intent ID from CheckoutFormPages
  const handlePaymentSuccess = async (actualPaymentIntentId, email) => {
    try {
      setIsLoading(true);
      if (promoInfo && promoInfo.type === 'free') {
        preparedFormData.append("intentID", "promo");
        preparedFormData.append("promoCode", promoInfo.promoCode);
        preparedFormData.append("user_email", email);
        setError(null);

        // Track Payment Completed event (free promo)
        if (typeof window.plausible === 'function') {
          window.plausible('Payment Completed', {
            revenue: { currency: 'USD', amount: 0 },
            props: {
              product: productIdMap[activeModal],
              product_name: activeModal,
              is_promo: true,
              promo_type: 'free'
            }
          });
        }

        // Navigate to processing page - this will handle the submission
        navigate('/processing', {
          state: {
            formDataEntries: Array.from(preparedFormData.entries()), // Convert FormData to entries array
            activeModal: activeModal
          }
        });
        return;
      }
      // Use the actual payment intent ID that was confirmed by Stripe, fallback to our stored one
      const paymentIntentToCheck = actualPaymentIntentId || intentID;
      preparedFormData.append("user_email", email);
      const response = await csrfService.fetch(`${STRIPE_SERVER_URL}/intent-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ "payment_intent_id": paymentIntentToCheck }),
      });

      const intent_result = await response.json();

      if (response.ok && intent_result.status === 'succeeded') {

        if (!preparedFormData) {
          console.error("Form data is null in handlePaymentSuccess");
          setError('Form data is missing. Please try again.');
          return;
        }

        setError(null);

        // Track Payment Completed event (paid or discounted)
        const finalAmount = getAmount(activeModal, promoInfo) / 100;
        if (typeof window.plausible === 'function') {
          window.plausible('Payment Completed', {
            revenue: { currency: 'USD', amount: finalAmount },
            props: {
              product: productIdMap[activeModal],
              product_name: activeModal,
              is_promo: !!promoInfo?.type,
              promo_type: promoInfo?.type || null
            }
          });
        }

        // Navigate to processing page - this will handle the submission
        navigate('/processing', {
          state: {
            formDataEntries: Array.from(preparedFormData.entries()), // Convert FormData to entries array
            activeModal: activeModal
          }
        });
      } else {
        // Handle specific error cases
        if (intent_result.status === 'requires_payment_method') {
          setError('Payment was not completed. Please try again with a valid payment method.');
        } else if (intent_result.status === 'used') {
          setError('This payment has already been used. Please start over.');
        } else {
          setError(intent_result.error || `Payment verification failed: ${intent_result.status || 'Unknown error'}`);
        }
      }

    } catch (error) {
      setError('Failed to verify payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBetaSubmit = () => {
    // For beta testing, go directly to processing
    navigate('/processing', {
      state: {
        formDataEntries: Array.from(preparedFormData.entries()),
        activeModal: activeModal
      }
    });
  };

  const handleApplyPromoCode = async (promoCode) => {
    // call backend to validate promo code
    const response = await csrfService.fetch(`${STRIPE_SERVER_URL}/promo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ "promoCode": promoCode, "request_type": activeModal }),
    });
    const promo_result = await response.json();

    if (response.ok && promo_result.success === true) {
      setPromoInfo(promo_result);
    } else {
      try {
        alert(`${promo_result.message}`);
      }
      catch (error) {
        alert('Invalid promo code.');
      }
    }
  };

  if (!formDataEntries || !activeModal) {
    return null;
  }

  return (
    <div className="homepage">
      {isSmallScreen && !smallScreenNoticeDismissed && (
        <div
          className="small-screen-notice"
          role="status"
          aria-live="polite"
        >
          <span className="small-screen-notice__icon" aria-hidden="true">💻</span>
          <span className="small-screen-notice__text">
            For the best checkout experience, we recommend using a laptop or desktop with a larger screen.
          </span>
          <button
            type="button"
            className="small-screen-notice__close"
            onClick={dismissSmallScreenNotice}
            aria-label="Dismiss notice"
          >
            ×
          </button>
        </div>
      )}
      <h1 className="centered-title"></h1>

      {error && (
        <div className="result-container error payment-container">
          {error}
          <button
            className="submit-button"
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Return to Home
          </button>
        </div>
      )}

      {!error && BETA_TESTING ? (
        <div className="result-container payment-container" style={{ maxWidth: '400px' }}>
          <h2>Beta Testing Mode</h2>
          <p>Using promo code: {betaInfo?.promoCode}</p>
          <p>Email: {betaInfo?.email}</p>
          <button
            className="submit-button"
            onClick={handleBetaSubmit}
            disabled={isLoading}
          >
            Continue to Processing
          </button>
        </div>
      ) : (
        !error && (clientSecret || promoInfo?.type === 'free') && preparedFormData && (
          <div className="result-container payment-container" style={{ maxWidth: '400px', padding: 0 }}>
            <Elements
              stripe={stripePromise}
              options={{ clientSecret }}
              key={clientSecret} // Add this line - it forces remounting when clientSecret changes
            >

              <CheckoutFormPages
                setTermsAccepted={setTermsAccepted}
                termsAccepted={termsAccepted}
                OnComplete={handlePaymentSuccess}
                handleApplyPromoCode={handleApplyPromoCode}
                amount={getAmount(activeModal, promoInfo) / 100}
                productName={activeModal}
                promoBypassed={promoInfo?.type === 'free'}
                email={userEmail}
                setEmail={setUserEmail}
              />

            </Elements>
          </div>
        )
      )}
    </div>
  );
};

export default PaymentPage;
